// Third party
import React, { ReactNode, useState, useRef, useEffect } from 'react';
import classNames from 'classnames';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';
import useScreenSize from '@/hooks/useScreenSize';
import useBodyScrollLock from '@/hooks/useBodyScrollLock';

// Components
import { Button } from '../../../components/buttons';

// Utils
import { FormattedMessage } from '@/utils/i18n';

type ComponentProps = {
  className?: string;
  leftItems?: ReactNode[];
  middleItems?: ReactNode[];
  mobileMenuItems: ReactNode[];
  onBack?: () => void;
};

type Props = ComponentProps;

const ComparisonTopBarNavigationMobile = ({ className, leftItems, middleItems, mobileMenuItems, onBack }: Props) => {
  const { isLightVersion } = useThemeVersion();
  const { isMobile } = useScreenSize();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);

  const classes = classNames('comparison-top-bar-navigation comparison-top-bar-navigation-mobile d-flex align-items-center', className, {
    'is-light': isLightVersion,
  });

  // Lock body scroll when menu is open
  useBodyScrollLock(isMenuOpen);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && navRef.current && !menuRef.current.contains(event.target as Node) && !navRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const handleToggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleCloseMenu = () => {
    setIsMenuOpen(false);
  };

  const handleBackClick = () => {
    if (isMenuOpen) {
      handleCloseMenu();
    } else if (onBack) {
      onBack();
    }
  };

  return (
    <>
      <nav ref={navRef} className={classes} data-testid="comparison-top-bar-navigation-mobile">
        <ul className="top-bar-nav-left">
          <li>
            <Button className="top-bar-button mobile-back-button" onClick={handleBackClick} icon="arrow-thin-left" aria-label="Back" />
          </li>
        </ul>

        <ul className="top-bar-nav-middle">
          {isMenuOpen ? (
            <li className="menu-title">
              <FormattedMessage id="SiteNavigation.Menu" />
            </li>
          ) : (
            !isMobile && middleItems?.map((node, index) => <li key={index}>{node}</li>)
          )}
        </ul>

        <ul className="top-bar-nav-far-right">
          {!isMenuOpen && leftItems && leftItems.length > 0 && (
            <>
              {leftItems.map((node, index) => (
                <li key={`action-${index}`} className="action-button-item">
                  {node}
                </li>
              ))}
            </>
          )}
          <li>
            <Button className="top-bar-button mobile-menu-button" onClick={handleToggleMenu} icon="ds-more-vertical" aria-label="Menu" />
          </li>
        </ul>
      </nav>

      {/* Custom dropdown menu - rendered outside nav for full width */}
      {isMenuOpen && (
        <div ref={menuRef} className={classNames('mobile-dropdown-menu', { 'is-light': isLightVersion })}>
          <div className="menu-section-title">
            <FormattedMessage id="Comparison.Details" defaultMessage="Comparison Details" />
          </div>
          <div className="menu-items-container">
            {mobileMenuItems.map((item, index) => (
              <React.Fragment key={index}>
                <div className="mobile-menu-item">{item}</div>
                <div className="menu-item-divider" />
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default ComparisonTopBarNavigationMobile;
